<script setup lang="ts">
import { useData } from "vitepress";
import { computed } from "vue";
import type { ProjectStat } from "../../../../projects/index.data";
import { formatDate } from "../../../utils/index.ts";
import StatBlock from "../../primitives/StatBlock.vue";

const { frontmatter } = useData();

const live = computed<string | undefined>(() => frontmatter.value.live);
const role = computed<string | undefined>(() => frontmatter.value.role);
const stack = computed<string[]>(() => frontmatter.value.stack || []);
const stats = computed<ProjectStat[]>(() => frontmatter.value.stats || []);

const year = computed(() => {
  const date = frontmatter.value.date;
  if (!date) return "";
  const match = formatDate(date).match(/\d{4}/);
  return match ? match[0] : "";
});

// Display form of the live URL: drop the scheme and any trailing slash.
const liveLabel = computed(() =>
  (live.value || "").replace(/^https?:\/\//, "").replace(/\/$/, ""),
);

const hasFacts = computed(
  () =>
    Boolean(role.value) ||
    Boolean(year.value) ||
    Boolean(live.value) ||
    stack.value.length > 0 ||
    stats.value.length > 0,
);
</script>

<template>
  <section v-if="hasFacts" class="project-facts surface">
    <dl class="fact-list">
      <div v-if="role" class="fact">
        <dt>Role</dt>
        <dd>{{ role }}</dd>
      </div>
      <div v-if="year" class="fact">
        <dt>Year</dt>
        <dd>{{ year }}</dd>
      </div>
      <div v-if="live" class="fact">
        <dt>Live</dt>
        <dd>
          <a :href="live" target="_blank" rel="noopener">{{ liveLabel }} ↗</a>
        </dd>
      </div>
      <div v-if="stack.length" class="fact fact--wide">
        <dt>Stack</dt>
        <dd>{{ stack.join(" · ") }}</dd>
      </div>
    </dl>

    <div v-if="stats.length" class="fact-stats">
      <StatBlock
        v-for="stat in stats"
        :key="stat.label"
        :value="stat.value"
        :label="stat.label"
        variant="compact"
      />
    </div>
  </section>
</template>

<style scoped lang="scss">
@use "../../styles/abstracts/breakpoints" as *;
@use "../../styles/abstracts/mixins";

.project-facts {
  @include mixins.surface;
  padding: var(--space-lg);
  border-radius: 12px;
  margin-bottom: var(--space-2xl);
}

.fact-list {
  display: grid;
  gap: var(--space-md);
  margin: 0;

  @include lg {
    grid-template-columns: repeat(3, 1fr);
  }
}

.fact--wide {
  @include lg {
    grid-column: 1 / -1;
  }
}

.fact dt {
  /* Sentence-case body text rather than tracked-out mono caps. Bumped a step
     from 2xs, which was sized for uppercase letterspacing and reads too small
     without it. */
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-muted);
  margin-bottom: var(--space-xs);
}

.fact dd {
  margin: 0;
}

.fact-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: var(--space-lg);
  margin-top: var(--space-xl);
  padding-top: var(--space-lg);
  border-top: 1px solid var(--color-border);
}
</style>
